import { connect } from 'react-redux';
import React from 'react';
import { formatCurrencyByLocale, obfuscateAccountNumber } from '@tink/core/src/utils';
import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { ConfigurationFeatureFlag } from '@tink/config/src/tinkLinkConfig';
import { StyledPaymentDetailsBanner } from '../../../../../DirectPayments/components/DirectPaymentDetailsBanner/DirectPaymentDetailsBanner';
import { useUrlQuery } from '../../../../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../../../../stores/redux-store';
import { Table, TableItem } from '../../../../../../core/atoms/Table/Table';
import { useProduct } from '../../../../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { ProductDimensionPay } from '../../../../../../models/product';
import { haveFeatureFlag } from '../../../../../../models/configuration';
import { useConfiguration as useConfigurationImport } from '../../../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { messages } from '../../../../../DirectPayments/components/DirectPaymentDetailsBanner/messages';

export type PaymentDetailsBannerProps = PropsFromRedux & {
  useConfiguration?: typeof useConfigurationImport;
};

export const PaymentDetailsBannerPresenter: React.FC<PaymentDetailsBannerProps> = ({
  paymentDetails,
  session,
  useConfiguration = useConfigurationImport,
}) => {
  const intl = useIntl();
  const { productDimension } = useProduct();

  let items: { content: TableItem[]; summary?: TableItem } = { content: [] };
  let footerText;

  const sourceAccountNumber = session?.sourceAccountNumber;
  const { data: configurationData } = useConfiguration();
  const { locale } = useUrlQuery();

  if (paymentDetails && productDimension === ProductDimensionPay.DIRECT) {
    const { amount, currency, executionDate, recipientName } = paymentDetails;

    const totalPaymentItem = {
      value: formatCurrencyByLocale(amount, currency, locale),
      label: intl.formatMessage(messages.totalPayment),
    };

    const payingToItem = {
      value: recipientName,
      label: intl.formatMessage(messages.payingTo),
    };

    const executionDateItem = executionDate
      ? [
          {
            value: executionDate,
            label: intl.formatMessage(messages.executionDate),
          },
        ]
      : [];

    // to be modified for 1SCA to show account being paid from, error caused from useProviderPaymentConditions hook so
    // temporarily removed payingFromItem from items list on line 78
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payingFromItem =
      sourceAccountNumber &&
      // temp feature flag for internal testing, remove when confirmed that logic works as intended
      haveFeatureFlag(
        configurationData,
        ConfigurationFeatureFlag.TEST_SKIPPING_AIS_CONFIGURATION_CHANGE
      )
        ? [
            {
              value: obfuscateAccountNumber(sourceAccountNumber).visiblePart,
              label: intl.formatMessage(messages.payingFrom),
            },
          ]
        : [];

    items = {
      content: [...payingFromItem, payingToItem, ...executionDateItem],
      summary: totalPaymentItem,
    };
  }
  const { summary, content } = items;

  return (
    <StyledPaymentDetailsBanner data-testid="paymentDetails">
      <Table content={content} summary={summary} footerText={footerText} />
    </StyledPaymentDetailsBanner>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    paymentDetails: state.paymentDetails.data,
    session: state.session.data,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const PaymentDetailsBanner = connector(PaymentDetailsBannerPresenter);
